/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id="' + id + '"]');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.pageTitles = {};
      this.hasMore = true;
    }
    connectedCallback() {
      this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.pageTitles[this.currentPage] = document.title;

      if (this.grid) {
        this.setupHistoryObserver();
        this.observeProducts(this.grid.querySelectorAll('[data-page]'));
      }

      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
    }
    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.historyObserver) {
        this.historyObserver.disconnect();
      }
    }
    getNextUrl() {
      const nextLink = this.querySelector('a[rel="next"]');
      if (nextLink && nextLink.getAttribute('href')) {
        return nextLink.getAttribute('href');
      }
      if (this.button && this.button.getAttribute('href')) {
        return this.button.getAttribute('href');
      }
      return null;
    }
    loadMore() {
      if (!this.button) {
        return;
      }
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadProducts();
        this.button.blur();
      });
    }
    infinite() {
      if (!this.getNextUrl()) {
        return;
      }
      const base = this;
      this.observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          base.loadProducts();
        }
      }, {
        threshold: 0.1
      });
      this.observer.observe(this);
    }
    loadProducts() {
      const url = this.getNextUrl();
      if (!url || this.getAttribute('loading')) {
        return;
      }
      this.setAttribute('loading', true);
      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          this.renderProducts(responseText, url);
          dispatchCustomEvent('pagination:page-changed', {
            url: url
          });
        })
        .catch(() => {
          this.removeAttribute('loading');
        });
    }
    renderProducts(html, url) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid_to_replace = doc.getElementById('product-grid');

      if (!grid_to_replace) {
        this.stopLoading();
        return;
      }

      const products = grid_to_replace.querySelectorAll('.column');
      if (!products.length) {
        this.stopLoading();
        return;
      }

      const loadedPagination = doc.querySelector('pagination-theme');
      const loadedPage = loadedPagination && loadedPagination.dataset.currentPage;
      const loadedTitle = doc.querySelector('title');
      if (loadedPage && loadedTitle) {
        this.pageTitles[loadedPage] = loadedTitle.textContent;
      }

      for (let i = 0; i < products.length; i++) {
        this.grid.appendChild(products[i]);
      }

      this.observeProducts(products);
      this.updateNextLinks(doc);

      if (this.animations_enabled) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: () => {
            this.finishLoading();
          }
        });
      } else {
        this.finishLoading();
      }
    }
    updateNextLinks(doc) {
      const newNext = doc.querySelector('pagination-theme a[rel="next"]');
      const currentNext = this.querySelector('a[rel="next"]');
      const newHref = newNext ? newNext.getAttribute('href') : null;

      if (newHref) {
        if (currentNext) {
          currentNext.setAttribute('href', newHref);
        }
        if (this.button) {
          this.button.setAttribute('href', newHref);
        }
        this.hasMore = true;
      } else {
        if (currentNext) {
          currentNext.remove();
        }
        this.hasMore = false;
        this.stopLoading();
      }
    }
    finishLoading() {
      this.removeAttribute('loading');
      if (typeof StampedFn !== 'undefined' && typeof StampedFn.loadBadges !== 'undefined') {
        StampedFn.loadBadges();
      }
      if (this.hasMore && this.classList.contains('pagination-type--infinite') && this.getNextUrl()) {
        const rect = this.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          this.loadProducts();
        }
      }
    }
    stopLoading() {
      this.hasMore = false;
      if (this.observer) {
        this.observer.unobserve(this);
      }
      this.removeAttribute('loading');
      if (this.button) {
        this.button.classList.add('visually-hidden');
      }
    }
    setupHistoryObserver() {
      this.historyObserver = new IntersectionObserver((entries) => {
        const intersecting = entries.filter((entry) => entry.isIntersecting);
        if (!intersecting.length) {
          return;
        }
        intersecting.sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
        const page = intersecting[0].target.dataset.page;
        if (page) {
          this.updateHistory(page);
        }
      }, {
        rootMargin: '-15% 0px -70% 0px',
        threshold: 0
      });
    }
    observeProducts(products) {
      if (!this.historyObserver) {
        return;
      }
      products.forEach((product) => {
        if (product.dataset.page) {
          this.historyObserver.observe(product);
        }
      });
    }
    updateHistory(page) {
      const pageNumber = parseInt(page, 10);
      if (!pageNumber || pageNumber === this.currentPage) {
        return;
      }

      const url = new URL(window.location.href);
      if (pageNumber <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(pageNumber));
      }

      const nextUrl = url.pathname + url.search + url.hash;
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      if (nextUrl === currentUrl) {
        this.currentPage = pageNumber;
        return;
      }

      this.currentPage = pageNumber;
      history.replaceState({
        page: pageNumber
      }, '', nextUrl);

      if (this.pageTitles[pageNumber]) {
        document.title = this.pageTitles[pageNumber];
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}
